import React, { useState, useEffect, useRef } from 'react';
import {
  RotateCcw,
  Database,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  ChevronDown,
  History,
  RefreshCw,
} from 'lucide-react';
import {
  listTrainingDatabases,
  executeRevert,
  getRevertStatus,
  getDatabaseBranches,
  retryRevert,
  getRevertHistory,
} from '../../services/trainingRevertApi';

const POLL_INTERVAL_MS = 3000;

// ─── Step indicator ───────────────────────────────────────────────────────────

const StepIcon = ({ status }) => {
  if (status === 'succeeded') return <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />;
  if (status === 'failed')    return <XCircle className="w-5 h-5 text-red-500 shrink-0" />;
  if (status === 'inProgress') return <Loader2 className="w-5 h-5 text-blue-500 animate-spin shrink-0" />;
  return <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-slate-600 shrink-0" />;
};

const Step = ({ step }) => (
  <div className="flex items-start gap-3 py-2">
    <StepIcon status={step.status} />
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-medium ${
        step.status === 'succeeded' ? 'text-green-700 dark:text-green-400' :
        step.status === 'failed'    ? 'text-red-700 dark:text-red-400' :
        step.status === 'inProgress' ? 'text-blue-700 dark:text-blue-400' :
        'text-gray-500 dark:text-slate-400'
      }`}>
        {step.name}
      </p>
      {step.message && (
        <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{step.message}</p>
      )}
    </div>
  </div>
);

// ─── Confirmation Modal ────────────────────────────────────────────────────────

const ConfirmModal = ({ selectedDb, selectedBranch, archiveName, onConfirm, onCancel, loading }) => {
  const [checked, setChecked] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Confirm Training Environment Revert
          </h2>
        </div>

        <div className="space-y-3 mb-5 text-sm text-gray-700 dark:text-gray-300">
          <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 space-y-1.5">
            <p><span className="font-medium">Current DB will be archived as:</span></p>
            <p className="font-mono text-xs text-orange-600 dark:text-orange-400">{archiveName}</p>
            <p className="mt-2"><span className="font-medium">Selected backup to restore:</span></p>
            <p className="font-mono text-xs text-blue-600 dark:text-blue-400">{selectedDb}</p>
            <p className="mt-2"><span className="font-medium">Schema branch to deploy:</span></p>
            <p className="font-mono text-xs text-purple-600 dark:text-purple-400">{selectedBranch}</p>
          </div>
          <p className="text-gray-500 dark:text-slate-400 text-xs">
            This operation will archive the current <strong>training-db</strong>,
            then create a copy of the selected backup. This cannot be undone automatically.
          </p>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer mb-5">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            I understand this will reset the training database and all current training data will be archived.
          </span>
        </label>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!checked || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Execute Revert
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const styles = {
    succeeded: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    failed:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    inProgress:'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    pending:   'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] ?? styles.pending}`}>
      {status === 'inProgress' && <Loader2 className="w-3 h-3 animate-spin" />}
      {status}
    </span>
  );
};

// ─── History section ───────────────────────────────────────────────────────────

const HistorySection = ({ onRetry }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  useEffect(() => {
    getRevertHistory(10)
      .then(setHistory)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRetry = async (operationId) => {
    setRetrying(operationId);
    try {
      await onRetry(operationId);
    } finally {
      setRetrying(null);
    }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-slate-500 py-4">
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading history…
    </div>
  );

  if (history.length === 0) return (
    <p className="text-sm text-gray-400 dark:text-slate-500 italic py-4">No previous operations found.</p>
  );

  return (
    <div className="space-y-2">
      {history.map((item) => (
        <div
          key={item.operationId}
          className="flex items-center justify-between gap-3 px-3 py-2.5 bg-gray-50 dark:bg-slate-700/50 rounded-lg"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={item.overallStatus} />
              <span className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate">
                {item.selectedDatabase}
              </span>
              <span className="text-xs text-gray-400 dark:text-slate-500 truncate">
                → {item.branch}
              </span>
            </div>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
              {new Date(item.startedAt).toLocaleString()}
              {item.archiveName && (
                <span className="ml-1.5 font-mono">({item.archiveName})</span>
              )}
            </p>
          </div>
          {item.overallStatus === 'failed' && (
            <button
              onClick={() => handleRetry(item.operationId)}
              disabled={retrying === item.operationId}
              className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 disabled:opacity-50"
            >
              {retrying === item.operationId
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
              Retry
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TrainingRevert = () => {
  const [phase, setPhase] = useState('idle');
  // idle → loading → select → confirming → executing → done

  const [databases, setDatabases] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedDb, setSelectedDb] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const pollRef = useRef(null);
  const dropdownRef = useRef(null);
  const branchDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(e.target)) setBranchDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Stop polling when done
  useEffect(() => {
    if (session?.overallStatus === 'succeeded' || session?.overallStatus === 'failed') {
      clearInterval(pollRef.current);
    }
  }, [session?.overallStatus]);

  // Cleanup on unmount
  useEffect(() => () => clearInterval(pollRef.current), []);

  const startPolling = (operationId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await getRevertStatus(operationId);
        setSession(status);
      } catch {
        // ignore transient poll errors
      }
    }, POLL_INTERVAL_MS);
  };

  const loadDatabases = async () => {
    setPhase('loading');
    setError(null);
    try {
      const [dbs, branchList] = await Promise.all([
        listTrainingDatabases(),
        getDatabaseBranches(),
      ]);
      setDatabases(dbs);
      setBranches(branchList);
      setPhase('select');
    } catch (err) {
      setError(err.message || 'Failed to load databases.');
      setPhase('idle');
    }
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      const { operationId } = await executeRevert(selectedDb, selectedBranch);

      setSession({
        operationId,
        overallStatus: 'inProgress',
        selectedDatabase: selectedDb,
        branch: selectedBranch,
        archiveName: '',
        steps: [
          { name: 'Archive current training-db', status: 'pending' },
          { name: `Copy ${selectedDb} → training-db`, status: 'pending' },
          { name: 'Reseed Invoice identity', status: 'pending' },
          { name: `Build Blackbird_Database (${selectedBranch})`, status: 'pending' },
          { name: 'Deploy schema → training', status: 'pending' },
        ],
      });

      setPhase('executing');
      startPolling(operationId);
    } catch (err) {
      setError(err.message || 'Failed to start revert.');
      setPhase('select');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleRetry = async (operationId) => {
    setRetrying(true);
    setError(null);
    try {
      await retryRevert(operationId);
      // Load the current session state then start polling
      const current = await getRevertStatus(operationId);
      setSession(current);
      setPhase('executing');
      startPolling(operationId);
    } catch (err) {
      setError(err.message || 'Failed to retry revert.');
    } finally {
      setRetrying(false);
    }
  };

  const handleReset = () => {
    clearInterval(pollRef.current);
    setPhase('idle');
    setDatabases([]);
    setBranches([]);
    setSelectedDb('');
    setSelectedBranch('');
    setSession(null);
    setError(null);
  };

  const archiveName = session?.archiveName ||
    `training-db_previous_${new Date().toISOString().slice(0, 10)}`;

  const isComplete = session?.overallStatus === 'succeeded';
  const isFailed   = session?.overallStatus === 'failed';

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
          <RotateCcw className="w-5 h-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Training Environment Revert</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Archive the current <code className="font-mono text-xs">training-db</code> and restore from a backup.
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
          <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Idle ── */}
      {phase === 'idle' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 text-center">
          <Database className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
            Load all available training backup databases to begin.
          </p>
          <button
            onClick={loadDatabases}
            className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Load Available Backups
          </button>
        </div>
      )}

      {/* ── Loading ── */}
      {phase === 'loading' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
          <span className="text-sm text-gray-500 dark:text-slate-400">Loading databases…</span>
        </div>
      )}

      {/* ── Select ── */}
      {phase === 'select' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select backup to restore
            </label>
            {databases.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-slate-500 italic">No training backup databases found.</p>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-left hover:border-blue-400 transition-colors"
                >
                  <span className={selectedDb ? 'text-gray-900 dark:text-gray-100 font-mono' : 'text-gray-400 dark:text-slate-500'}>
                    {selectedDb || 'Choose a database…'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                </button>
                {dropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg overflow-auto max-h-60">
                    {databases.map((db) => (
                      <button
                        key={db}
                        onClick={() => { setSelectedDb(db); setDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm font-mono hover:bg-blue-50 dark:hover:bg-slate-600 transition-colors ${
                          db === selectedDb ? 'bg-blue-50 dark:bg-slate-600 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {db}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select schema branch to deploy
            </label>
            {branches.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-slate-500 italic">No branches found.</p>
            ) : (
              <div className="relative" ref={branchDropdownRef}>
                <button
                  onClick={() => setBranchDropdownOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-left hover:border-purple-400 transition-colors"
                >
                  <span className={selectedBranch ? 'text-gray-900 dark:text-gray-100 font-mono' : 'text-gray-400 dark:text-slate-500'}>
                    {selectedBranch || 'Choose a branch…'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                </button>
                {branchDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg overflow-auto max-h-60">
                    {branches.map((branch) => (
                      <button
                        key={branch}
                        onClick={() => { setSelectedBranch(branch); setBranchDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm font-mono hover:bg-purple-50 dark:hover:bg-slate-600 transition-colors ${
                          branch === selectedBranch ? 'bg-purple-50 dark:bg-slate-600 text-purple-700 dark:text-purple-300' : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {branch}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedDb && selectedBranch && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-xs space-y-1">
              <p className="text-orange-700 dark:text-orange-300">
                <span className="font-semibold">Current training-db</span> will be archived as:
              </p>
              <p className="font-mono text-orange-600 dark:text-orange-400">{archiveName}</p>
              <p className="text-orange-700 dark:text-orange-300 mt-1.5">
                <span className="font-semibold">{selectedDb}</span> will be copied and renamed to{' '}
                <span className="font-mono">training-db</span>.
              </p>
              <p className="text-orange-700 dark:text-orange-300 mt-1.5">
                Schema from branch <span className="font-semibold font-mono">{selectedBranch}</span> will be built and deployed.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setPhase('confirming')}
              disabled={!selectedDb || !selectedBranch}
              className="px-5 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Revert Training Environment
            </button>
          </div>
        </div>
      )}

      {/* ── Confirmation modal ── */}
      {phase === 'confirming' && (
        <ConfirmModal
          selectedDb={selectedDb}
          selectedBranch={selectedBranch}
          archiveName={archiveName}
          loading={confirmLoading}
          onConfirm={handleConfirm}
          onCancel={() => setPhase('select')}
        />
      )}

      {/* ── Executing / Done ── */}
      {(phase === 'executing' || phase === 'done') && session && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Revert Progress</h2>
            {session.overallStatus === 'inProgress' && (
              <span className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Running…
              </span>
            )}
          </div>

          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {session.steps?.map((step, i) => <Step key={i} step={step} />)}
          </div>

          {/* Result banners */}
          {isComplete && (
            <div className="flex items-center gap-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>
                Training environment restored successfully.{' '}
                <span className="font-mono text-xs">training-db</span> is now ready.
              </span>
            </div>
          )}

          {isFailed && (
            <div className="flex items-start gap-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Revert failed.</p>
                {session.archiveName && (
                  <p className="text-xs mt-0.5">
                    If the archive step succeeded, the old database was saved as{' '}
                    <span className="font-mono">{session.archiveName}</span>.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            {isFailed && (
              <button
                onClick={() => handleRetry(session.operationId)}
                disabled={retrying}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Retry from last step
              </button>
            )}
            {(isComplete || isFailed) && (
              <button onClick={handleReset} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                Start over
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── History ── */}
      {phase === 'idle' && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Recent Operations</h2>
          </div>
          <HistorySection onRetry={async (operationId) => {
            await handleRetry(operationId);
          }} />
        </div>
      )}
    </div>
  );
};

export default TrainingRevert;
